/**
 * Sidebar variant for the "Learning" (University) contour.
 *
 * Body: quick stats (active skills, sessions this week), sub-navigation
 * (Skills / Tuners / Training / Marketplace), and a recent-activity feed
 * (last 10 training sessions). Mirrors the visual contract of the
 * Departments sidebar — same account header, theme switch, sign-out.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { ChevronsLeft, ChevronsUpDown, LogOut, Moon, Settings, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { authApi } from '../../lib/api';
import { learningApi, learningKeys } from '../../lib/api/learning';
import { useResizablePanel } from '../../lib/use-resizable-panel';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { useThemeStore } from '../../stores/theme-store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { fmtRelative } from './formatters';

interface UniversitySidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function UniversitySidebar({ collapsed, onToggle }: UniversitySidebarProps) {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();
  const user = useAuthStore((s) => s.user);
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  // Role is currently unused in the sidebar — every University surface
  // is visible to authenticated users; mutations are gated on each route.
  void workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role;
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  const activeWorkspace = workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.workspace;

  const { width, isDragging, onHandleMouseDown } = useResizablePanel({
    storageKey: 'chaos-sidebar-width',
    defaultWidth: 260,
    minWidth: 220,
    maxWidth: 380,
    side: 'left',
  });

  const summaryQ = useQuery({
    queryKey: learningKeys.summary(),
    queryFn: () => learningApi.getSummary(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const recentSessionsQ = useQuery({
    queryKey: learningKeys.trainingSessionsList({ limit: 10 }),
    queryFn: () => learningApi.listTrainingSessions({ limit: 10 }),
    staleTime: 30_000,
  });
  const recentSessions = recentSessionsQ.data?.sessions ?? [];

  const handleSignOut = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    setUnauthenticated();
    window.location.href = '/login';
  };

  if (collapsed) return null;

  const isUniversityHome = !!matchRoute({ to: '/university' });
  const isSkills = !!matchRoute({ to: '/university/skills', fuzzy: true });
  const isTuners = !!matchRoute({ to: '/university/tuners', fuzzy: true });
  const isTraining = !!matchRoute({ to: '/university/training', fuzzy: true });
  const isMarketplace = !!matchRoute({ to: '/university/marketplace', fuzzy: true });
  const isPatterns = !!matchRoute({ to: '/university/patterns', fuzzy: true });
  const isAbTests = !!matchRoute({ to: '/university/ab-tests', fuzzy: true });

  return (
    <aside
      aria-label="University navigation"
      style={{ width }}
      className={cn(
        'relative flex h-screen shrink-0 flex-col border-r border-border bg-muted/30',
        isDragging && 'select-none',
      )}
    >
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group flex flex-1 items-center justify-between px-3 py-2.5 text-left hover:bg-foreground/5"
              aria-label="Account menu"
            >
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-[13px] font-semibold tracking-tight text-foreground">
                  {activeWorkspace?.name ?? user?.displayName ?? user?.email ?? t('app.name')}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {user?.email ?? ''}
                </span>
              </div>
              <ChevronsUpDown
                className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{user?.displayName ?? user?.email}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === 'dark' ? t('nav.light_mode') : t('nav.dark_mode')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/profile">
                <Settings className="h-4 w-4" />
                <span>{t('settings.profile.title')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
              <span>{t('dashboard.sign_out')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Collapse sidebar"
          className="mr-1.5 flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <ChevronsLeft className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-2 pt-3">
        <div className="px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          University
        </div>
        <ul className="mt-1 flex flex-col">
          <NavRow to="/university" active={isUniversityHome} label="Overview" />
          <NavRow to="/university/skills" active={isSkills} label="Skills" />
          <NavRow to="/university/tuners" active={isTuners} label="Tuners" />
          <NavRow to="/university/training" active={isTraining} label="Training sessions" />
          <NavRow to="/university/patterns" active={isPatterns} label="Patterns" />
          <NavRow to="/university/ab-tests" active={isAbTests} label="A/B tests" />
          <NavRow to="/university/marketplace" active={isMarketplace} label="Marketplace" />
        </ul>

        <div className="mt-4 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Stats
        </div>
        <ul className="mt-1 flex flex-col gap-0.5 px-2 text-[11px] text-muted-foreground">
          <StatRow label="Active skills" value={summaryQ.data?.activeSkillsCount ?? null} />
          <StatRow
            label="Sessions this week"
            value={summaryQ.data?.trainingSessionsThisWeek ?? null}
          />
          <StatRow label="Active sessions" value={summaryQ.data?.trainingSessionsActive ?? null} />
          <StatRow label="Total exports" value={summaryQ.data?.totalExports ?? null} />
        </ul>

        <div className="mt-4 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Recent sessions
        </div>
        <ul className="mt-1 flex flex-col">
          {recentSessions.length === 0 ? (
            <li className="px-2 py-1 text-[11px] text-muted-foreground/70">No sessions yet.</li>
          ) : (
            recentSessions.slice(0, 10).map((s) => (
              <li key={s.id}>
                <Link
                  to="/university/training/$sessionId"
                  params={{ sessionId: s.id }}
                  className="flex items-center gap-1.5 rounded px-2 py-1 text-[12px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
                  title={s.skillName ?? s.id}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      s.status === 'completed' && 'bg-success',
                      s.status === 'active' && 'bg-warning',
                      s.status === 'awaiting_tuner' && 'bg-warning',
                      s.status === 'abandoned' && 'bg-destructive',
                      s.status === 'planned' && 'bg-muted-foreground/40',
                    )}
                  />
                  <span className="truncate">{s.skillName ?? 'Untitled'}</span>
                  <span className="ml-auto truncate text-muted-foreground/60">
                    {fmtRelative(s.startedAt)}
                  </span>
                </Link>
              </li>
            ))
          )}
        </ul>
      </nav>

      <button
        type="button"
        aria-label="Resize sidebar"
        tabIndex={-1}
        onMouseDown={onHandleMouseDown}
        className={cn(
          'absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize border-0 bg-transparent p-0',
          'transition-colors',
          isDragging ? 'bg-foreground/20' : 'hover:bg-foreground/10',
        )}
      />
    </aside>
  );
}

interface NavRowProps {
  to:
    | '/university'
    | '/university/skills'
    | '/university/tuners'
    | '/university/training'
    | '/university/marketplace'
    | '/university/patterns'
    | '/university/ab-tests';
  active: boolean;
  label: string;
}

function NavRow({ to, active, label }: NavRowProps) {
  return (
    <li>
      <Link
        to={to}
        className={cn(
          'flex items-center rounded px-2 py-1 text-[13px] transition-colors',
          active
            ? 'bg-foreground/5 text-foreground'
            : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
        )}
      >
        {label}
      </Link>
    </li>
  );
}

function StatRow({ label, value }: { label: string; value: number | null }) {
  return (
    <li className="flex items-baseline justify-between">
      <span>{label}</span>
      <span className="font-mono tabular-nums text-foreground">{value == null ? '—' : value}</span>
    </li>
  );
}
